import { useMemo } from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import Modal from "../../../components/Modal";
import Button from "../../../components/Button";
import {
  Section,
  SectionHeader,
  NoDataText,
} from "../../components/SectionComponents";
import {
  ModalContent,
  ModalHeader,
  ModalBody,
  TableHeader,
  TableRow,
  OldValue,
  ChangePill,
  SectionTitle,
  Footer,
} from "./DiffComponents";
import {
  BulkConditionDiff,
  BulkCellChange,
  useBulkEditStateStore,
} from "./bulkEditState";
import {
  parseBomPath,
  resolveItemContext,
  resolveProcessContext,
} from "../../utils/conditions";

type BulkDiffModalProps = {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  diffSummary: BulkConditionDiff[];
  changeCount: number;
  isSaving?: boolean;
};

const formatDiffValue = (value: string): string => {
  return value === "" ? "(empty)" : value;
};

/**
 * Build a human-readable label for the BOM/process target a change applies
 * to. This is the "which component within the condition" identifier — used
 * to group changes into per-component sub-sections inside each condition's
 * card, since BulkDiffModal (unlike ReplacementDiffModal) has no
 * BOMNavigation panel to communicate that context.
 *
 * The label resolves the actual item record from the baseline tree so users
 * see the item type + name (e.g., "Anode: ABC-123") rather than a raw BOM
 * path. A 1-indexed `#n` suffix is appended only when a sibling at the same
 * BOM level would produce an identical label; same disambiguation rule for
 * processes. Falls back to the legacy path-based label if the baseline
 * can't be resolved (rare — only on baseline drift).
 */
const getTargetLabel = (
  change: BulkCellChange,
  baseline: FormspecCondition | undefined
): string => {
  if (change.target.type === "condition") {
    return "Condition Details";
  }

  const { itemWithBom, parentBom, leafIndex } = resolveItemContext(
    baseline,
    change.target.path
  );
  const indices = parseBomPath(change.target.path);

  let baseLabel: string;
  if (itemWithBom) {
    const item = itemWithBom.item;
    baseLabel = `${item.item_type_name}: ${item.name}`;
    const itemAmbiguous = parentBom?.some(
      (sib, i) =>
        i !== leafIndex &&
        sib.item.item_type_name === item.item_type_name &&
        sib.item.name === item.name
    );
    if (itemAmbiguous) {
      baseLabel += ` #${leafIndex + 1}`;
    }
  } else {
    // Fallback: structural path string so the header is never blank.
    baseLabel =
      indices.length === 0
        ? "Root Item"
        : `BOM ${indices.map((i) => String(i + 1)).join(" > ")}`;
  }

  if (change.target.type === "attribute") {
    return baseLabel;
  }

  const processCtx = resolveProcessContext(
    itemWithBom?.processes,
    change.target.processPath || []
  );

  let processLabel: string;
  if (processCtx) {
    const { leafProcess, siblings, leafIndex: leafProcessIndex } = processCtx;
    const processAmbiguous = siblings.some(
      (sib, i) =>
        i !== leafProcessIndex &&
        sib.process.process_type.name === leafProcess.process_type.name
    );
    processLabel = processAmbiguous
      ? `${leafProcess.process_type.name} #${leafProcessIndex + 1}`
      : leafProcess.process_type.name;
  } else {
    processLabel = "Process";
  }

  return `${baseLabel} > ${processLabel}`;
};

type ComponentGroup = {
  label: string;
  changes: BulkCellChange[];
};

/**
 * Within a single condition, group changes by their target component
 * (resolved item + optional process) and sort each group's rows
 * alphabetically by column header for a stable, scannable display.
 *
 * Internally keys by `${label}::${path}::${processPath}` so two distinct
 * components that happen to render identical labels still get separate
 * cards. The path/processPath portion of the key is never displayed.
 */
const groupChangesByComponent = (
  changes: BulkCellChange[],
  baseline: FormspecCondition | undefined
): ComponentGroup[] => {
  const byKey = new Map<string, { label: string; changes: BulkCellChange[] }>();
  for (const change of changes) {
    const label = getTargetLabel(change, baseline);
    if (change.target.type === "condition") {
      const key = `${label}::condition::${change.target.field}`;
      const existing = byKey.get(key);
      if (existing) {
        existing.changes.push(change);
      } else {
        byKey.set(key, { label, changes: [change] });
      }
      continue;
    }

    const processPathPart =
      change.target.type === "parameter"
        ? (change.target.processPath || []).join(">")
        : "";
    const key = `${label}::${change.target.path}::${processPathPart}`;
    const existing = byKey.get(key);
    if (existing) {
      existing.changes.push(change);
    } else {
      byKey.set(key, { label, changes: [change] });
    }
  }

  return Array.from(byKey.values())
    .sort((a, b) => a.label.localeCompare(b.label))
    .map(({ label, changes: groupChanges }) => ({
      label,
      changes: groupChanges
        .slice()
        .sort((a, b) => a.columnHeader.localeCompare(b.columnHeader)),
    }));
};

const BulkDiffModal = ({
  open,
  onClose,
  onConfirm,
  diffSummary,
  changeCount,
  isSaving = false,
}: BulkDiffModalProps) => {
  const baselineByConditionId = useBulkEditStateStore(
    (state) => state.baselineByConditionId
  );

  const groupedDiffSummary = useMemo(
    () =>
      diffSummary.map((group) => ({
        ...group,
        componentGroups: groupChangesByComponent(
          group.changes,
          baselineByConditionId[group.conditionId]
        ),
      })),
    [diffSummary, baselineByConditionId]
  );

  const isEmpty = diffSummary.length === 0;

  return (
    <Modal
      open={open}
      onClose={(_event, reason) => {
        if (reason === "backdropClick" && isSaving) {
          return;
        }
        onClose();
      }}
      maxWidth={false}
      fullWidth
      PaperProps={{
        sx: {
          width: "90vw",
          maxHeight: "90vh",
          maxWidth: "90vw",
        },
      }}
    >
      <ModalContent>
        <ModalHeader>
          <Typography variant="h6">Review Bulk Changes</Typography>
          <Typography variant="body2" color="textSecondary">
            {`You have ${changeCount} pending cell change(s) across ${diffSummary.length} condition(s).`}
          </Typography>
        </ModalHeader>

        <ModalBody>
          {isEmpty ? (
            <NoDataText>No pending changes</NoDataText>
          ) : (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 2,
                overflow: "auto",
                flex: 1,
                minHeight: 0,
              }}
            >
              {groupedDiffSummary.map((group) => (
                <Section key={group.conditionId}>
                  <SectionHeader>
                    <SectionTitle>
                      <span>
                        {`Condition ${group.conditionId}: ${group.conditionName}`}
                      </span>
                      <ChangePill>
                        {group.changes.length} change
                        {group.changes.length === 1 ? "" : "s"}
                      </ChangePill>
                    </SectionTitle>
                  </SectionHeader>
                  {group.componentGroups.map((componentGroup) => (
                    <Box
                      key={`${group.conditionId}-${componentGroup.label}`}
                      sx={{
                        marginBottom: 2,
                        "&:last-child": { marginBottom: 0 },
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          padding: "8px 12px",
                          backgroundColor: "#fff",
                          borderTop: "1px solid #e0e0e0",
                          borderLeft: "1px solid #e0e0e0",
                          borderRight: "1px solid #e0e0e0",
                          borderTopLeftRadius: 4,
                          borderTopRightRadius: 4,
                          fontSize: "13px",
                          fontWeight: 600,
                          color: "#333",
                        }}
                      >
                        <span>{componentGroup.label}</span>
                        <ChangePill>
                          {componentGroup.changes.length} change
                          {componentGroup.changes.length === 1 ? "" : "s"}
                        </ChangePill>
                      </Box>
                      <Box
                        sx={{
                          border: "1px solid #e0e0e0",
                          borderTop: "none",
                          borderBottomLeftRadius: 4,
                          borderBottomRightRadius: 4,
                          overflow: "hidden",
                        }}
                      >
                        <TableHeader>
                          <span>Name</span>
                          <span>Old value</span>
                          <span>New value</span>
                        </TableHeader>
                        {componentGroup.changes.map((change) => (
                          <TableRow key={change.key}>
                            <span>{change.columnHeader}</span>
                            <OldValue>
                              {formatDiffValue(change.previousDisplay)}
                            </OldValue>
                            <span>
                              {formatDiffValue(change.currentDisplay)}
                            </span>
                          </TableRow>
                        ))}
                      </Box>
                    </Box>
                  ))}
                </Section>
              ))}
            </Box>
          )}
        </ModalBody>

        <Footer>
          <Button color="tertiary" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button
            color="primary"
            onClick={() => onConfirm()}
            disabled={isSaving || changeCount === 0}
          >
            {isSaving ? (
              <>
                Saving{" "}
                <CircularProgress
                  color="inherit"
                  size={20}
                  style={{ marginLeft: 8 }}
                />
              </>
            ) : (
              `Save Changes (${changeCount})`
            )}
          </Button>
        </Footer>
      </ModalContent>
    </Modal>
  );
};

export default BulkDiffModal;
